import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_CARD } from './table-management/designTokens';

interface PotentialMatch {
  id: string;
  fullName: string;
  ticketNumber: string | null;
}

interface AuditIssue {
  registrationId: string;
  userFullName: string;
  userEmail: string;
  eventName: string;
  hasGuestRecord: boolean;
  hasTicket: boolean;
  guestRecordId: string | null;
  ticketId: string | null;
  ticketNumber: number | null;
  issue: string;
  recommendedAction: 'delete_orphan' | 'create_guest_record' | 'link_existing';
  potentialMatches: PotentialMatch[];
}

interface AuditResult {
  totalRegistrations: number;
  issuesFound: number;
  healthyCount: number;
  issues: AuditIssue[];
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const ACTION_LABELS: Record<string, string> = {
  delete_orphan: 'Delete Orphan',
  create_guest_record: 'Create Guest Record',
  link_existing: 'Link to Existing',
};

const ACTION_COLORS: Record<string, string> = {
  delete_orphan: '#d32f2f',
  create_guest_record: PRIMARY,
  link_existing: '#1565c0',
};

export function RegistrationIntegrityAuditModal({ open, onClose }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<AuditResult | null>(null);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [actionResults, setActionResults] = useState<Record<string, { success: boolean; message: string }>>({});
  const [linkTarget, setLinkTarget] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      runAudit();
    } else {
      setResult(null);
      setError('');
      setActionResults({});
      setLinkTarget({});
    }
  }, [open]);

  async function runAudit() {
    setLoading(true);
    setError('');
    setActionResults({});
    try {
      const data = await apiClient<AuditResult>('/legacy-ball/registration-audit');
      setResult(data);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to run audit.');
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteOrphan(registrationId: string) {
    if (!confirm('Are you sure you want to delete this orphan registration? This cannot be undone.')) return;
    setActionLoading((prev) => ({ ...prev, [registrationId]: true }));
    try {
      await apiClient(`/legacy-ball/registration-audit/${registrationId}`, { method: 'DELETE' });
      setActionResults((prev) => ({ ...prev, [registrationId]: { success: true, message: 'Deleted successfully' } }));
      // Remove from issues list
      setResult((prev) =>
        prev ? { ...prev, issuesFound: prev.issuesFound - 1, issues: prev.issues.filter((i) => i.registrationId !== registrationId) } : prev,
      );
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setActionResults((prev) => ({ ...prev, [registrationId]: { success: false, message: apiErr.message || 'Delete failed' } }));
    } finally {
      setActionLoading((prev) => ({ ...prev, [registrationId]: false }));
    }
  }

  async function handleCreateGuestRecord(registrationId: string) {
    setActionLoading((prev) => ({ ...prev, [registrationId]: true }));
    try {
      await apiClient(`/legacy-ball/registration-audit/${registrationId}/create-guest`, { method: 'POST' });
      setActionResults((prev) => ({ ...prev, [registrationId]: { success: true, message: 'Guest record created' } }));
      // Remove from issues list
      setResult((prev) =>
        prev ? { ...prev, issuesFound: prev.issuesFound - 1, issues: prev.issues.filter((i) => i.registrationId !== registrationId) } : prev,
      );
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setActionResults((prev) => ({ ...prev, [registrationId]: { success: false, message: apiErr.message || 'Create failed' } }));
    } finally {
      setActionLoading((prev) => ({ ...prev, [registrationId]: false }));
    }
  }

  async function handleLinkExisting(registrationId: string) {
    const guestRecordId = linkTarget[registrationId];
    if (!guestRecordId) {
      setActionResults((prev) => ({ ...prev, [registrationId]: { success: false, message: 'Select a guest record to link' } }));
      return;
    }
    setActionLoading((prev) => ({ ...prev, [registrationId]: true }));
    try {
      await apiClient(`/legacy-ball/registration-audit/${registrationId}/link`, {
        method: 'POST',
        body: { guestRecordId },
      });
      setActionResults((prev) => ({ ...prev, [registrationId]: { success: true, message: 'Linked successfully' } }));
      // Remove from issues list
      setResult((prev) =>
        prev ? { ...prev, issuesFound: prev.issuesFound - 1, issues: prev.issues.filter((i) => i.registrationId !== registrationId) } : prev,
      );
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setActionResults((prev) => ({ ...prev, [registrationId]: { success: false, message: apiErr.message || 'Link failed' } }));
    } finally {
      setActionLoading((prev) => ({ ...prev, [registrationId]: false }));
    }
  }

  if (!open) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Registration Integrity Audit</h2>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close audit modal">✕</button>
        </div>

        {loading && <div style={styles.loadingState}>Running audit...</div>}
        {error && <div style={styles.errorState}>{error}</div>}

        {result && !loading && (
          <>
            {/* Summary */}
            <div style={styles.summary}>
              <div style={styles.summaryCard}>
                <div style={styles.summaryNumber}>{result.totalRegistrations}</div>
                <div style={styles.summaryLabel}>Total</div>
              </div>
              <div style={{ ...styles.summaryCard, borderColor: '#4caf50' }}>
                <div style={{ ...styles.summaryNumber, color: '#4caf50' }}>{result.healthyCount}</div>
                <div style={styles.summaryLabel}>Healthy</div>
              </div>
              <div style={{ ...styles.summaryCard, borderColor: result.issuesFound > 0 ? '#ff9800' : '#4caf50' }}>
                <div style={{ ...styles.summaryNumber, color: result.issuesFound > 0 ? '#ff9800' : '#4caf50' }}>{result.issuesFound}</div>
                <div style={styles.summaryLabel}>Issues</div>
              </div>
            </div>

            {result.issuesFound === 0 && (
              <div style={styles.successBanner}>
                All registrations are properly linked to GuestRecords and Tickets.
              </div>
            )}

            {/* Issues List */}
            {result.issues.length > 0 && (
              <div style={styles.issuesList}>
                <div style={{ fontSize: '0.85rem', color: TEXT_MUTED, marginBottom: '0.25rem' }}>
                  Showing {result.issues.length} issue{result.issues.length !== 1 ? 's' : ''} below:
                </div>
                {result.issues.map((issue) => {
                  const actionResult = actionResults[issue.registrationId];
                  const isLoading = actionLoading[issue.registrationId];

                  return (
                    <div key={issue.registrationId} style={styles.issueCard}>
                      <div style={styles.issueHeader}>
                        <div>
                          <div style={styles.issueName}>{issue.userFullName}</div>
                          <div style={styles.issueEmail}>{issue.userEmail}</div>
                        </div>
                        <div style={styles.issueBadges}>
                          <span style={issue.hasGuestRecord ? styles.badgeGreen : styles.badgeRed}>
                            {issue.hasGuestRecord ? '✓ Guest' : '✗ No Guest'}
                          </span>
                          <span style={issue.hasTicket ? styles.badgeGreen : styles.badgeRed}>
                            {issue.hasTicket ? `✓ Ticket #${issue.ticketNumber}` : '✗ No Ticket'}
                          </span>
                        </div>
                      </div>

                      <div style={styles.issueBody}>
                        <div style={styles.issueDescription}>{issue.issue}</div>
                        <div style={styles.recommendation}>
                          <span style={styles.recommendLabel}>Recommended:</span>
                          <span style={{ ...styles.recommendAction, color: ACTION_COLORS[issue.recommendedAction] }}>
                            {ACTION_LABELS[issue.recommendedAction]}
                          </span>
                        </div>

                        {/* Action buttons */}
                        <div style={styles.actions}>
                          {issue.recommendedAction === 'delete_orphan' && (
                            <button
                              style={{ ...styles.actionBtn, backgroundColor: '#d32f2f', color: '#fff' }}
                              onClick={() => handleDeleteOrphan(issue.registrationId)}
                              disabled={isLoading}
                              aria-label={`Delete orphan registration for ${issue.userFullName}`}
                            >
                              {isLoading ? 'Deleting...' : '🗑️ Delete Orphan'}
                            </button>
                          )}

                          {issue.recommendedAction === 'create_guest_record' && (
                            <button
                              style={{ ...styles.actionBtn, backgroundColor: PRIMARY, color: '#fff' }}
                              onClick={() => handleCreateGuestRecord(issue.registrationId)}
                              disabled={isLoading}
                              aria-label={`Create guest record for ${issue.userFullName}`}
                            >
                              {isLoading ? 'Creating...' : '➕ Create Guest Record'}
                            </button>
                          )}

                          {issue.recommendedAction === 'link_existing' && (
                            <div style={styles.linkSection}>
                              <select
                                style={styles.linkSelect}
                                value={linkTarget[issue.registrationId] || ''}
                                onChange={(e) => setLinkTarget((prev) => ({ ...prev, [issue.registrationId]: e.target.value }))}
                                aria-label={`Select guest record to link for ${issue.userFullName}`}
                              >
                                <option value="">Select a match...</option>
                                {issue.potentialMatches.map((m) => (
                                  <option key={m.id} value={m.id}>
                                    {m.fullName}{m.ticketNumber ? ` (${m.ticketNumber})` : ''}
                                  </option>
                                ))}
                              </select>
                              <button
                                style={{ ...styles.actionBtn, backgroundColor: '#1565c0', color: '#fff' }}
                                onClick={() => handleLinkExisting(issue.registrationId)}
                                disabled={isLoading || !linkTarget[issue.registrationId]}
                                aria-label={`Link registration to guest record for ${issue.userFullName}`}
                              >
                                {isLoading ? 'Linking...' : '🔗 Link'}
                              </button>
                            </div>
                          )}

                          {/* Secondary actions (always available) */}
                          {issue.recommendedAction !== 'delete_orphan' && !issue.hasGuestRecord && !issue.hasTicket && (
                            <button
                              style={{ ...styles.secondaryBtn }}
                              onClick={() => handleDeleteOrphan(issue.registrationId)}
                              disabled={isLoading}
                              aria-label={`Delete orphan registration for ${issue.userFullName}`}
                            >
                              🗑️ Delete Instead
                            </button>
                          )}

                          {issue.recommendedAction !== 'create_guest_record' && !issue.hasGuestRecord && (
                            <button
                              style={{ ...styles.secondaryBtn }}
                              onClick={() => handleCreateGuestRecord(issue.registrationId)}
                              disabled={isLoading}
                              aria-label={`Create guest record for ${issue.userFullName}`}
                            >
                              ➕ Create Instead
                            </button>
                          )}
                        </div>

                        {/* Action result */}
                        {actionResult && (
                          <div style={actionResult.success ? styles.actionSuccess : styles.actionError}>
                            {actionResult.message}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Refresh */}
            <div style={styles.footer}>
              <button style={styles.refreshBtn} onClick={runAudit} disabled={loading}>
                🔄 Re-run Audit
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: BG_CARD,
    borderRadius: '12px',
    padding: '1.5rem',
    width: '95%',
    maxWidth: '750px',
    maxHeight: '85vh',
    overflowY: 'auto' as const,
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.25rem',
  },
  title: {
    fontSize: '1.2rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.2rem',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    lineHeight: 1,
    color: TEXT_MUTED,
  },
  loadingState: {
    textAlign: 'center',
    padding: '2rem',
    color: TEXT_MUTED,
    fontSize: '0.95rem',
  },
  errorState: {
    textAlign: 'center',
    padding: '1rem',
    color: '#d32f2f',
    backgroundColor: '#ffebee',
    borderRadius: '6px',
    fontSize: '0.9rem',
  },
  summary: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.25rem',
  },
  summaryCard: {
    flex: 1,
    textAlign: 'center',
    padding: '0.75rem',
    borderRadius: '8px',
    border: `1px solid ${BORDER_LIGHT}`,
    backgroundColor: '#fafafa',
  },
  summaryNumber: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: TEXT_DARK,
  },
  summaryLabel: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    marginTop: '2px',
  },
  successBanner: {
    padding: '1rem',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    borderRadius: '6px',
    textAlign: 'center',
    fontSize: '0.95rem',
    fontWeight: 500,
  },
  issuesList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
    paddingBottom: '0.5rem',
  },
  issueCard: {
    border: '1px solid #d0d0d0',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  issueHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    backgroundColor: '#f3f4f6',
    borderBottom: '1px solid #d0d0d0',
    flexWrap: 'wrap' as const,
    gap: '0.5rem',
  },
  issueName: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: TEXT_DARK,
  },
  issueEmail: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
  },
  issueBadges: {
    display: 'flex',
    gap: '0.4rem',
  },
  badgeGreen: {
    fontSize: '0.75rem',
    padding: '2px 8px',
    borderRadius: '10px',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 500,
  },
  badgeRed: {
    fontSize: '0.75rem',
    padding: '2px 8px',
    borderRadius: '10px',
    backgroundColor: '#ffebee',
    color: '#c62828',
    fontWeight: 500,
  },
  issueBody: {
    padding: '0.75rem 1rem',
  },
  issueDescription: {
    fontSize: '0.82rem',
    color: '#b71c1c',
    backgroundColor: '#fff3e0',
    padding: '0.3rem 0.6rem',
    borderRadius: '4px',
    marginBottom: '0.5rem',
    fontWeight: 500,
  },
  recommendation: {
    marginBottom: '0.6rem',
    fontSize: '0.85rem',
  },
  recommendLabel: {
    color: TEXT_MUTED,
    marginRight: '0.4rem',
  },
  recommendAction: {
    fontWeight: 600,
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  actionBtn: {
    padding: '0.4rem 0.75rem',
    border: 'none',
    borderRadius: '5px',
    fontSize: '0.8rem',
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  secondaryBtn: {
    padding: '0.35rem 0.7rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '5px',
    fontSize: '0.78rem',
    fontWeight: 500,
    cursor: 'pointer',
    backgroundColor: '#fff',
    color: TEXT_MUTED,
    whiteSpace: 'nowrap' as const,
  },
  linkSection: {
    display: 'flex',
    gap: '0.4rem',
    alignItems: 'center',
  },
  linkSelect: {
    padding: '0.35rem 0.5rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    fontSize: '0.8rem',
    maxWidth: '220px',
  },
  actionSuccess: {
    marginTop: '0.5rem',
    fontSize: '0.8rem',
    color: '#2e7d32',
    backgroundColor: '#e8f5e9',
    padding: '0.3rem 0.6rem',
    borderRadius: '4px',
  },
  actionError: {
    marginTop: '0.5rem',
    fontSize: '0.8rem',
    color: '#d32f2f',
    backgroundColor: '#ffebee',
    padding: '0.3rem 0.6rem',
    borderRadius: '4px',
  },
  footer: {
    marginTop: '1rem',
    display: 'flex',
    justifyContent: 'center',
  },
  refreshBtn: {
    padding: '0.5rem 1.25rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
};
